import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { Space, Button, Input, Form, Typography, Tag, Tooltip, Empty, Spin, Tabs, Dropdown, Progress } from 'antd';
import type { MenuProps } from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  SearchOutlined,
  EyeOutlined,
  DollarOutlined,
  CalendarOutlined,
  WalletOutlined,
  CreditCardOutlined,
  DownloadOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  RightOutlined,
  SubnodeOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import Paginations from '../../components/pagination';
import { getServiceParams } from '../../actions/commonActions';
import FilterPopover from '../../components/filter';
import ConfirmModal from '../../components/confirmModal';
import ExpenseFormModal from '../../components/modals/expenseFormModal';
import FeeFormModal from '../../components/modals/feeFormModal';
import { expenseActions } from '../../actions/expenseActions';
import { feeActions } from '../../actions/feeActions';
import { userActions } from '../../actions/userActions';
import TableData from '../../components/table';
import { categoryActions } from '../../actions/categoryActions';
import GuardedButton from '../../components/guardedButton';
import ExpenseDetailPanel from './expenseDetailPanel';
import FeeDetailPanel from './feeDetailPanel';
import { getblobLink } from '../../services/helper';
import { useCurrentPageAccess } from '../../services/helper';
import SpinnerLoader from '../../components/spinnerLoader';


const { Text } = Typography;

export interface FieldSpec {
  name: string;
  label: string;
  path?: string;
  type?: 'text' | 'email' | 'phone' | 'date' | 'textarea' | 'tags' | 'dropdown';
  editable?: boolean;
  rules?: any[];
  placeholder?: string;
  options?: Array<{ label: string; value: any; disabled?: boolean }>;
  formatter?: (value: any, item: any) => React.ReactNode;
}

interface QueryExpense {
  key: string;
  expenseName: string;
  description?: string;
  amount: number;
  expenseDate: string;
  categoryName: string;
  serviceName: string;
  studentName: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

interface QueryFee {
  key: string;
  studentId: string;
  categoryId: string;
  serviceId: string;
  enrollmentId?: string;
  studentName: string;
  gender: string;
  categoryName: string;
  serviceName: string;
  totalFee: number;
  paidAmount: number;
  pendingAmount: number;
  paymentDate: string;
  paymentMode: string;
  remarks?: string;
  createdBy?: string;
  createdAt: string;
  feeEntries?: any[];
}

interface ApiExpense {
  _id: string;
  expenseName: string;
  description?: string;
  amount: number;
  expenseDate: string;
  categoryId?: {
    _id: string;
    categoryName: string;
  };
  serviceId?: {
    _id: string;
    serviceName: string;
  };
  createdBy?: {
    _id: string;
    name: string;
  };
  updatedBy?: {
    _id: string;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
}

const Expenses: React.FC<{ role?: string }> = ({ role }) => {
  const hasFetchedInitialData = useRef(false);
  const hasFetchedFeeData = useRef(false);
  const navigate = useNavigate();
  const pageAccess = useCurrentPageAccess();
  const [activeTab, setActiveTab] = useState('expense');
  const [expandedExpenseKey, setExpandedExpenseKey] = useState<string | null>(null);
  const [expenseEditMode, setExpenseEditMode] = useState(false);
  const [expenseEditForm] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [feeSearchText, setFeeSearchText] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFeeModalOpen, setIsFeeModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [feeForm] = Form.useForm();
  const [filterData, setIsFilterData] = useState({
    serviceType: '',
    studentId: '',
    startDate: '',
    endDate: ''
  });
  const [feeFilterData, setFeeFilterData] = useState({
    studentId: '',
    startDate: '',
    endDate: '',
    paymentMode: ''
  });
  const [isFilterData, setIsFilter] = useState(false);
  const [isFeeFilter, setIsFeeFilter] = useState(false);
  const dispatch = useDispatch();
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<any | null>(null);
  const [recordToDelete, setRecordToDelete] = useState<string>('');
  const [feeRecordToDelete, setFeeRecordToDelete] = useState<string>('');
  const [selectedFeeRecord, setSelectedFeeRecord] = useState<QueryFee | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<Record<string, any>>({});
  const [appliedFeeFilters, setAppliedFeeFilters] = useState<Record<string, any>>({});
  const [mutableExpenses, setMutableExpenses] = useState<any[]>([]);
  const [mutableFees, setMutableFees] = useState<any[]>([]);
  const [isStudent, setIsStudent] = useState(false);
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [debouncedFeeSearchText, setDebouncedFeeSearchText] = useState('');
  const expenseView = useSelector((state: any) => state.getExpenseViewReducer);

  const defaultPayLoad = { pageNumber: 1, pageSize: 10 };
  const [pageSize, setPageSize] = useState<number>(Number(defaultPayLoad.pageSize));
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [feePageSize, setFeePageSize] = useState<number>(Number(defaultPayLoad.pageSize));
  const [feeCurrentPage, setFeeCurrentPage] = useState<number>(1);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [initialFeeLoaded, setInitialFeeLoaded] = useState(false);


  const getExpense = useSelector((state: any) => state.getExpenseParamsReducer);
  const getFee = useSelector((state: any) => state.getFeeParamsReducer);
  const exportFeeState = useSelector((state: any) => state.exportFeeReducer);
  const isFeeLoading = getFee?.isLoading;
  const location = useLocation();
  const feeState = location.state?.from;
  const totalRecords = getExpense?.expensedata?.totalRecords || getExpense?.expensedata?.totalExpenses || 0;
  const totalFeeRecords = getFee?.feedata?.totalRecords || getFee?.feedata?.totalFees || 0;

  const expenseData = useSelector((state: any) => state.createExpenseReducer);
  const feeData = useSelector((state: any) => state.createFeeReducer);
  const deleteExpenseState = useSelector((state: any) => state.deleteExpenseReducer);
  const deleteFeeState = useSelector((state: any) => state.deleteFeeReducer);
  const categoryData = useSelector((state: any) => state.getCategoryParamsReducer);
  const serviceData = useSelector((state: any) => state.getServiceParamsReducer);
  const studentData = useSelector((state: any) => state.userListReducer);
  const roleListData = useSelector((state: any) => state.roleDataReducer);

  const categoryOptions = useMemo(() => {
    const payload = categoryData?.categorydata;
    const list = payload?.categories ?? payload ?? [];
    return (Array.isArray(list) ? list : []).map((c: any) => ({
      label: c?.categoryName,
      value: c?._id,
    }));
  }, [categoryData]);

 const serviceOptions = useMemo(() => {
  const payload = serviceData?.servicedata;
  const list = payload?.services ?? payload ?? [];
  return (Array.isArray(list) ? list : []).map((s: any) => ({
    label: s?.serviceName,
    value: s?._id,
    categoryId: s?.category?._id ?? s?.category,
  }));
}, [serviceData]);

  const studentOptions = useMemo(() => {
    const users = studentData?.userList?.users ?? [];
    return (Array.isArray(users) ? users : []).map((s: any) => {
      const name = `${s?.firstName || ''} ${s?.lastName || ''}`.trim();
      const studentId = s?.enrolledNumber || s?.studentId;
      const displayLabel = studentId ? `${name} (${studentId})` : name;
      return {
        label: displayLabel,
        value: s?._id,
        enrolledNumber: studentId
      };
    });
  }, [studentData]);

  useEffect(() => {
    if (feeState) { setActiveTab('fees'); }
  }, [feeState]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const filterParam = params.get('tab');

    if (filterParam === 'fees') {
      setActiveTab('fees');
    }
  }, [location.search]);

  useEffect(() => {
    if (expenseData && expenseData.isSuccess) {
      fetchData(currentPage);
      setIsModalOpen(false);
      setIsEditMode(false);
      setExpenseEditMode(false);
      setSelectedExpense(null);
      form.resetFields();
      expenseData.isSuccess = false;
      // Refresh the open detail panel so it reflects the saved values.
      if (expandedExpenseKey) {
        dispatch(expenseActions.getExpenseView(expandedExpenseKey));
      }
    }
    if (expenseView && expenseView.isSuccess) {
      const expense = expenseView?.expenseview ?? null;
      setSelectedExpense(expense);
      expenseView.isSuccess = false;
    }
    if (deleteExpenseState && deleteExpenseState.isSuccess) {
      fetchData(currentPage);
      setRecordToDelete('');
      setExpandedExpenseKey(null);
      setExpenseEditMode(false);
      deleteExpenseState.isSuccess = false;
      dispatch(expenseActions.resetDeleteExpense());
    }
  }, [expenseData, expenseView, deleteExpenseState, currentPage, pageSize, dispatch, form, expandedExpenseKey]);

  // Collapse any open detail panel whenever the result set changes underneath it.
  useEffect(() => {
    setExpandedExpenseKey(null);
    setExpenseEditMode(false);
  }, [currentPage, pageSize, debouncedSearchText, isFilterData, filterData]);

  // Fee effects
  useEffect(() => {
    if (feeData && feeData.isSuccess) {
      fetchFeeData(feeCurrentPage);
      setIsFeeModalOpen(false);
      feeForm.resetFields();
      feeData.isSuccess = false;
    }
    if (deleteFeeState && deleteFeeState.isSuccess) {
      fetchFeeData(feeCurrentPage);
      setFeeRecordToDelete('');
      deleteFeeState.isSuccess = false;
      dispatch(feeActions.resetDeleteFee());
    }
  }, [feeData, deleteFeeState, feeCurrentPage, feePageSize, dispatch, feeForm]);

  const apiExpenses: ApiExpense[] = useMemo(() => {
    const expensedata = getExpense?.expensedata;
    console.log("called:", getExpense);

    return expensedata?.data ?? expensedata?.expenses ?? [];
  }, [getExpense?.expensedata]);

  const apiFees = useMemo(() => {
    const feedata = getFee?.feedata;
    return feedata?.data ?? feedata?.fees ?? [];
  }, [getFee?.feedata]);

  useEffect(() => {
    if (roleListData && roleListData.roleData) {
      dispatch(userActions.userListRequest({ role: roleListData?.roleData?.find((r: any) => r.roleName === 'Student')?._id }));
    }
  }, [roleListData]);

  useEffect(() => {

    if (expenseData?.isLoading) return;
    if (getExpense?.isLoading) return;

    const mapped: QueryExpense[] = apiExpenses.map((c: any) => ({
      key: c._id,
      expenseName: c.expenseName,
      categoryName: c.category?.categoryName || c.categoryId?.categoryName || '',
      serviceName: c.service?.serviceName || c.serviceId?.serviceName || '',
      studentName: c.student ? `${c.student.firstName || ''} ${c.student.lastName || ''}`.trim() : '-',
      amount: c.amount,
      expenseDate: c.expenseDate,
      description: c.description,
      createdAt: c.createdAt,
      updatedAt: c.updatedAt,
      createdBy: c.createdBy,
    }));

    if (mapped.length > 0) {
      setMutableExpenses(mapped);
    } else {
      setMutableExpenses([]);
    }
  }, [apiExpenses]);

  useEffect(() => {

    const mapped: QueryFee[] = apiFees.map((f: any) => ({
      key: f._id,
      studentId: f.studentId?._id || f.studentId || f.student?._id || '',
      categoryId: f.categoryId?._id || f.categoryId || f.category?._id || '',
      serviceId: f.serviceId?._id || f.serviceId || f.service?._id || '',
      enrollmentId: f.enrollmentId?._id || f.enrollmentId || '',
      studentName: f.student ? `${f.student.firstName || ''} ${f.student.lastName || ''}`.trim() : '-',
      categoryName: f.category?.categoryName || f.categoryId?.categoryName || '',
      serviceName: f.service?.serviceName || f.serviceId?.serviceName || '',
      totalFee: f.totalFee,
      paidAmount: f.paidAmount,
      pendingAmount: f.pendingAmount,
      paymentDate: f.paymentDate,
      gender: f.student?.gender || f.studentId?.gender || f.gender || 'male',
      paymentMode: f.paymentMode,
      remarks: f.remarks,
      createdAt: f.createdAt,
      createdBy: f.createdBy?.name || f.createdBy,
      feeEntries: (f.feeEntries || []).map((entry: any) => ({
        key: entry._id,
        paidAmount: entry.paidAmount,
        pendingAmount: entry.pendingAmount,
        paymentDate: entry.paymentDate,
        paymentMode: entry.paymentMode,
        remarks: entry.remarks,
        receiptNumber: entry.receiptNumber,
        createdAt: entry.createdAt,
        createdBy: entry.createdBy?.name || entry.createdBy,
      })),
    }));

    console.log("mapped fees:", mapped);

    setMutableFees(mapped);
  }, [apiFees]);

  // Expense search debounce
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchText(searchText.trim());
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    if (debouncedSearchText) {
      setCurrentPage(1);
    }
  }, [debouncedSearchText]);

  // Fee search debounce
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedFeeSearchText(feeSearchText.trim());
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [feeSearchText]);

  useEffect(() => {
    if (debouncedFeeSearchText) {
      setFeeCurrentPage(1);
    }
  }, [debouncedFeeSearchText]);

  // Expense data fetch
  useEffect(() => {

    const payload: any = {
      pageNumber: currentPage,
      pageSize
    };

    if (isFilterData && filterData && typeof filterData === 'object') {
      Object.entries(filterData).forEach(([k, v]) => {
        if (v !== undefined && v !== null && String(v).trim() !== '') {
          payload[k] = v;
        }
      });
    }

    dispatch(expenseActions.getExpenseParams(payload));
    dispatch(getServiceParams({ pageNumber: 1, pageSize: 100 }));
    dispatch(categoryActions.getCategoryParams({ pageNumber: 1, pageSize: 100 }));
    dispatch(userActions.userRoleRequest());

  }, [dispatch, currentPage, pageSize, isFilterData, filterData]);

  useEffect(() => {
    const payload: any = {
      pageNumber: currentPage,
      pageSize
    };

    if (debouncedSearchText) {
      payload.searchText = debouncedSearchText;
    }
    dispatch(expenseActions.getExpenseParams(payload));

  }, [debouncedSearchText])

  // Fee data fetch
  useEffect(() => {
    if (activeTab === 'fees') {
      dispatch(categoryActions.getCategoryParams({ pageNumber: 1, pageSize: 100 }));
      dispatch(getServiceParams({ pageNumber: 1, pageSize: 100 }));
      dispatch(userActions.userRoleRequest());

      const payload: any = {
        pageNumber: feeCurrentPage,
        pageSize: feePageSize
      };

      if (debouncedFeeSearchText) {
        payload.searchText = debouncedFeeSearchText;
      }

      if (isFeeFilter && feeFilterData && typeof feeFilterData === 'object') {
        Object.entries(feeFilterData).forEach(([k, v]) => {
          if (v !== undefined && v !== null && String(v).trim() !== '') {
            payload[k] = v;
          }
        });
      }

      dispatch(feeActions.getFeeParams(payload));
    }
  }, [dispatch, activeTab, feeCurrentPage, feePageSize, debouncedFeeSearchText, isFeeFilter, feeFilterData]);

  const fetchData = useCallback((page: number = 1, size: number = pageSize) => {
    const trimmedSearch = searchText?.trim();
    const payload: any = {
      pageNumber: page,
      pageSize: size,
      ...(trimmedSearch ? { searchText: trimmedSearch } : {}),
      ...(appliedFilters.serviceType && { serviceType: appliedFilters.serviceType }),
      ...(appliedFilters.categoryId && { categoryId: appliedFilters.categoryId }),
      ...(appliedFilters.serviceId && { serviceId: appliedFilters.serviceId }),
      ...(appliedFilters.studentId && { studentId: appliedFilters.studentId }),
      ...(appliedFilters.startDate && { startDate: appliedFilters.startDate }),
      ...(appliedFilters.endDate && { endDate: appliedFilters.endDate }),
    };
    dispatch(expenseActions.getExpenseParams(payload));
  }, [dispatch, searchText, pageSize, appliedFilters]);

  const fetchFeeData = useCallback((page: number = 1, size: number = feePageSize) => {
    const trimmedSearch = feeSearchText?.trim();
    const payload: any = {
      pageNumber: page,
      pageSize: size,
      ...(trimmedSearch ? { searchText: trimmedSearch } : {}),
      ...(appliedFeeFilters.studentId && { studentId: appliedFeeFilters.studentId }),
      ...(appliedFeeFilters.paymentMode && { paymentMode: appliedFeeFilters.paymentMode }),
      ...(appliedFeeFilters.startDate && { startDate: appliedFeeFilters.startDate }),
      ...(appliedFeeFilters.endDate && { endDate: appliedFeeFilters.endDate }),

    };
    dispatch(feeActions.getFeeParams(payload));
  }, [dispatch, feeSearchText, feePageSize, appliedFeeFilters]);

  useEffect(() => {
    if (!hasFetchedInitialData.current) {
      fetchData(defaultPayLoad.pageNumber, defaultPayLoad.pageSize);
      hasFetchedInitialData.current = true;
    }
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === 'fees' && !hasFetchedFeeData.current) {
      fetchFeeData(defaultPayLoad.pageNumber, defaultPayLoad.pageSize);
      hasFetchedFeeData.current = true;
    }
  }, [activeTab, fetchFeeData]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 2000);
    return () => clearTimeout(handler);
  }, [searchText]);

  // useEffect(() => {
  //   if (hasFetchedInitialData.current) {
  //     setCurrentPage(1);
  //     fetchData(1, pageSize);
  //   }
  // }, [debouncedSearchText, fetchData, pageSize]);


  const handleAdd = () => {
    setIsModalOpen(true);
  };


  const handleExpenseExpand = (expanded: boolean, record: any) => {
    setExpenseEditMode(false);
    expenseEditForm.resetFields();
    if (expanded) {
      setExpandedExpenseKey(String(record.key));
      dispatch(expenseActions.getExpenseView(record.key));
    } else {
      setExpandedExpenseKey(null);
    }
  };

  const handleExpensePanelEdit = (detail: any) => {
    expenseEditForm.resetFields();
    expenseEditForm.setFieldsValue({
      expenseName: detail?.expenseName,
      amount: detail?.amount,
      category: detail?.category?._id,
      service: detail?.service?._id,
      student: detail?.student?._id,
      expenseDate: detail?.expenseDate ? dayjs(detail.expenseDate) : null,
      description: detail?.description,
    });
    setExpenseEditMode(true);
  };

  const handleExpensePanelCancel = () => {
    setExpenseEditMode(false);
    expenseEditForm.resetFields();
  };

  const handleExpensePanelSave = (detail: any) => {
    expenseEditForm.validateFields().then((values) => {
      dispatch(expenseActions.createExpense({
        _id: detail?._id,
        expenseName: values.expenseName,
        categoryId: values.category !== undefined ? values.category : (detail?.category?._id ?? null),
        serviceId: values.service !== undefined ? values.service : (detail?.service?._id ?? null),
        studentId: values.student !== undefined ? values.student : (detail?.student?._id ?? null),
        amount: values.amount,
        expenseDate: values.expenseDate
          ? (typeof values.expenseDate === 'string' ? values.expenseDate : values.expenseDate.format('YYYY-MM-DD'))
          : detail?.expenseDate,
        description: values.description || '',
      }));
    });
  };

  const handleAddFee = (record?: QueryFee) => {
    setSelectedFeeRecord(record || null);
    setIsFeeModalOpen(true);
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
  };

  const handleFeeModalCancel = () => {
    setIsFeeModalOpen(false);
    setSelectedFeeRecord(null);
  };

  const handleExpenseFormFinish = (values: any) => {
    if (isEditMode && selectedExpense) {
      dispatch(expenseActions.createExpense({ ...values, _id: selectedExpense._id }));
    } else {
      dispatch(expenseActions.createExpense(values));
    }
  };

  const handleFeeFormFinish = (values: any) => {
    dispatch(feeActions.createFee(values));
    setSelectedFeeRecord(null);
  };

  const handleConfirmDelete = () => {
    if (recordToDelete) {
      dispatch(expenseActions.deleteExpense(recordToDelete));
      setSelectedExpense(null);
    }
  };

  const handleConfirmFeeDelete = () => {
    if (feeRecordToDelete) {
      dispatch(feeActions.deleteFee(feeRecordToDelete));
    }
  };

  const handleCancelDelete = () => {
    setRecordToDelete('');
  };

  const handleCancelFeeDelete = () => {
    setFeeRecordToDelete('');
  };

  const handleApplyFilters = (filters: Record<string, any>) => {
    setIsFilter(true);
    const applied: any = { ...(filters || {}) };
    if (applied.startDate) {
      if (typeof applied.startDate !== 'string' && applied.startDate && typeof applied.startDate.format === 'function') {
        applied.startDate = applied.startDate.format('YYYY-MM-DD');
      } else {
        applied.startDate = String(applied.startDate);
      }
    }
    if (applied.endDate) {
      if (typeof applied.endDate !== 'string' && applied.endDate && typeof applied.endDate.format === 'function') {
        applied.endDate = applied.endDate.format('YYYY-MM-DD');
      } else {
        applied.endDate = String(applied.endDate);
      }
    }

    setAppliedFilters(applied);
    setIsFilterData(applied);
    const hasAny = Object.values(applied).some(v => v !== undefined && v !== null && String(v).trim() !== '');
    setIsFilter(hasAny);
    setCurrentPage(1);
  };

  const handleApplyFeeFilters = (filters: Record<string, any>) => {
    setIsFeeFilter(true);
    const applied: any = { ...(filters || {}) };
    if (applied.startDate) {
      if (typeof applied.startDate !== 'string' && applied.startDate && typeof applied.startDate.format === 'function') {
        applied.startDate = applied.startDate.format('YYYY-MM-DD');
      } else {
        applied.startDate = String(applied.startDate);
      }
    }
    if (applied.endDate) {
      if (typeof applied.endDate !== 'string' && applied.endDate && typeof applied.endDate.format === 'function') {
        applied.endDate = applied.endDate.format('YYYY-MM-DD');
      } else {
        applied.endDate = String(applied.endDate);
      }
    }

    setAppliedFeeFilters(applied);
    setFeeFilterData(applied);
    const hasAny = Object.values(applied).some(v => v !== undefined && v !== null && String(v).trim() !== '');
    setIsFeeFilter(hasAny);
    setFeeCurrentPage(1);
  };

  const handlePageChange = (page: number, newPageSize?: number) => {
    const effectivePageSize = newPageSize ?? pageSize;
    setCurrentPage(page);
    setPageSize(effectivePageSize);
    fetchData(page, effectivePageSize);
  };

  const handleFeePageChange = (page: number, newPageSize?: number) => {
    const effectivePageSize = newPageSize ?? feePageSize;
    setFeeCurrentPage(page);
    setFeePageSize(effectivePageSize);
    fetchFeeData(page, effectivePageSize);
  };

  const handleClearFilters = () => {
    setAppliedFilters({});
    setIsFilter(false);
    setIsFilterData({ endDate: '', startDate: '', serviceType: '', studentId: '' });
    setCurrentPage(1);
  };

  const handleClearFeeFilters = () => {
    setAppliedFeeFilters({});
    setIsFeeFilter(false);
    setFeeFilterData({ endDate: '', startDate: '', studentId: '', paymentMode: '' });
    setFeeCurrentPage(1);
  };

  const handleFeeExport = (format: 'pdf' | 'excel') => {
    const payload: any = { format };
    if (feeFilterData && typeof feeFilterData === 'object') {
      Object.entries(feeFilterData).forEach(([k, v]) => {
        if (v !== undefined && v !== null && String(v).trim() !== '') {
          payload[k] = v;
        }
      });
    }
    if (feeSearchText && feeSearchText.trim()) {
      payload.searchText = feeSearchText.trim();
    }
    dispatch(feeActions.exportFee(payload));
  };

  const feeExportMenuItems: MenuProps['items'] = [
    {
      key: 'pdf',
      label: 'Export as PDF',
      icon: <FilePdfOutlined />,
      onClick: () => handleFeeExport('pdf'),
    },
    {
      key: 'excel',
      label: 'Export as Excel',
      icon: <FileExcelOutlined />,
      onClick: () => handleFeeExport('excel'),
    },
  ];

  const fields: FieldSpec[] = [
    { name: 'expenseName', label: 'Expense Name', type: 'text', editable: true },
    { name: 'category', label: 'Category', type: 'dropdown', editable: true, path: 'category.categoryName', options: categoryOptions, },
    { name: 'service', label: 'Service', type: 'dropdown', editable: true, path: 'service.serviceName', options: serviceOptions, },
    { name: 'student', label: 'Student', type: 'dropdown', editable: true, path: 'student.firstName', options: studentOptions, formatter: (value: any) => value ? `${value.firstName || ''} ${value.lastName || ''}`.trim() : '-' },
    { name: 'amount', label: 'Amount', type: 'text', editable: true, formatter: (value: number) => `₹${value?.toLocaleString('en-IN') || 0}` },
    { name: 'expenseDate', label: 'Expense Date', type: 'date', editable: true, formatter: (value: string) => value ? dayjs(value).format('DD/MM/YYYY') : '-' },
    { name: 'description', label: 'Description', type: 'textarea', editable: true },
    { name: 'createdAt', label: 'Created At', type: 'date', editable: false, formatter: (value: string) => value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-' },
    { name: 'updatedAt', label: 'Updated At', type: 'date', editable: false, formatter: (value: string) => value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-' }
  ];

  const columns = [
    {
      title: 'Expense Name', dataIndex: 'expenseName', key: 'expenseName', ellipsis: true,
      render: (text: string) => (<Text className="expense-name-text">{text}</Text>),
    },
    {
      title: 'Service / Category', key: 'service',
      render: (_: any, record: QueryExpense) => {
        if (!record.serviceName && !record.categoryName) {
          return <span>-</span>;
        }
        return (
          <div className="enrollment-service-info">
            <span className="enrollment-service-name">{record.serviceName}</span>
            <div className="enrollment-category-child">
              <SubnodeOutlined className="enrollment-category-child-icon" />
              <span className="enrollment-category-child-text">{record.categoryName}</span>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Student', dataIndex: 'studentName', key: 'studentName', ellipsis: true,
      render: (text: string) => (<Text>{text || '-'}</Text>),
    },
    {
      title: 'Amount', dataIndex: 'amount', key: 'amount', align: 'right' as const,
      render: (amount: number) => (<span className="expense-amount-chip"> <DollarOutlined /> ₹{amount?.toLocaleString('en-IN') || 0}</span>),
    },
    {
      title: 'Expense Date', dataIndex: 'expenseDate', key: 'expenseDate', render: (date: string) => (
        <span className="expense-date-text"> <CalendarOutlined /> {date ? dayjs(date).format('DD/MM/YYYY') : '-'}</span>),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (_: any, record: QueryExpense) => (
        <Space orientation="vertical" >
          {record.createdAt ? (
            <Text type="secondary" className="text-small">
              {new Date(record.createdAt).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}
            </Text>
          ) : (
            <Text type="secondary" className="text-small">-</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Created By', dataIndex: 'createdBy', key: 'createdBy', ellipsis: true,
      render: (_: any, record: QueryExpense) => (<Text className="expense-createdby-text">{record.createdBy}</Text>)
    },
  ];

  const filterFields = [
    { name: 'startDate', label: 'Start Date', type: 'date' as const, options: [] as any, },
    { name: 'endDate', label: 'End Date', type: 'date' as const, options: [] as any, },
    { name: 'serviceType', label: 'Service Type',placeholder: 'Select service', type: 'select' as const, options: [{ label: 'All', value: '' }, { label: 'Service', value: 'SERVICE' }, { label: 'Training', value: 'TRAINING' }] },
    { name: 'studentId', label: 'Student', type: 'select' as const, options: studentOptions, showSearch: true, placeholder: 'Select student' },
  ];

  const feeFilterFields = [
    { name: 'startDate', label: 'Start Date', type: 'date' as const, options: [] as any, },
    { name: 'endDate', label: 'End Date', type: 'date' as const, options: [] as any, },
    { name: 'studentId', label: 'Student', type: 'select' as const, options: studentOptions, showSearch: true, placeholder: 'Select student' },
    {
      name: 'paymentMode', label: 'Payment Mode', type: 'select' as const, options: [
        { label: 'All', value: '' },
        { label: 'Cash', value: 'CASH' },
        { label: 'UPI', value: 'UPI' },
        { label: 'Card', value: 'CARD' },
        { label: 'Net Banking', value: 'NET_BANKING' },
        { label: 'Cheque', value: 'CHEQUE' },
        { label: 'Bank Transfer', value: 'BANK_TRANSFER' },
      ]
    },
  ];

  const feeColumns = [
    {
      title: 'Student',
      dataIndex: 'studentName',
      key: 'studentName',
      render: (text: string, record: QueryFee) => {
        const pct = record.totalFee > 0 ? Math.round((record.paidAmount / record.totalFee) * 100) : 0;
        return (
          <div className="fee-student-cell">
            <div className="fee-student-avatar-wrap">
              <img src={record.gender === 'Male' ? `${getblobLink()}/public/asset/male-avatar.png` : `${getblobLink()}/public/asset/female-avatar.png`} alt="student" className="fee-student-avatar-icon" />
            </div>
            <div className="fee-student-info">
              <span className="fee-student-name">{text || '-'}</span>
              <div className="fee-student-progress">
                <Progress
                  percent={pct}
                  size="small"
                  showInfo={false}
                  strokeColor={pct === 100 ? '#22c55e' : '#6366f1'}
                  trailColor="#e5e7eb"
                  style={{ marginBottom: 0, width: 80 }}
                />
                <span className="fee-student-pct">{pct}%</span>
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Service / Category', key: 'service',
      render: (_: any, record: QueryFee) => (
        <div className="enrollment-service-info">
          <span className="enrollment-service-name">{record.serviceName}</span>
          <div className="enrollment-category-child">
            <SubnodeOutlined className="enrollment-category-child-icon" />
            <span className="enrollment-category-child-text">{record.categoryName}</span>
          </div>
        </div>
      ),
    },

    {
      title: 'Quota Target',
      dataIndex: 'totalFee',
      key: 'totalFee',
      align: 'center' as const,
      render: (amount: number) => (
        <span className="fee-amount fee-amount--neutral">
          ₹{amount?.toLocaleString('en-IN') || '0'}
        </span>
      ),
    },
    {
      title: 'Total Paid',
      dataIndex: 'paidAmount',
      key: 'paidAmount',
      align: 'center' as const,
      render: (amount: number, record: QueryFee) => {
        const pct = record.totalFee > 0 ? (amount / record.totalFee) * 100 : 0;
        return (
          <span className={`fee-amount ${pct >= 100 ? 'fee-amount--green' : 'fee-amount--blue'}`}>
            ₹{amount?.toLocaleString('en-IN') || '0'}
          </span>
        );
      },
    },
    {
      title: 'Balance',
      dataIndex: 'pendingAmount',
      key: 'pendingAmount',
      align: 'center' as const,
      render: (amount: number) => (
        <span className={`fee-amount`}>
          ₹{amount?.toLocaleString('en-IN') || '0'}
          {amount === 0 && (
            <CheckCircleOutlined className="fee-paid-icon" />
          )}
          {amount > 0 && (
            <ClockCircleOutlined className="fee-pending-icon" />
          )}
        </span>
      ),
    },
    {
      title: 'Payments',
      key: 'paymentCount',
      align: 'center' as const,
      render: (_: any, record: QueryFee) => {
        const count = record.feeEntries?.length || 0;
        return (
          <span className={`fee-count-badge ${count > 0 ? 'fee-count-badge--active' : 'fee-count-badge--empty'}`}>
            {count}
          </span>
        );
      },
    },
    {
      title: 'Last Payment',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      width: 140,
      render: (date: string) => (
        <span className="fee-date-cell">
          <CalendarOutlined />
          {date ? dayjs(date).format('DD MMM YYYY') : <span className="fee-date-empty">—</span>}
        </span>
      ),
    },
    ...(pageAccess.isAdd ? [{
      title: '',
      key: 'actions',
      width: 48,
      render: (_: any, record: QueryFee) => (
        <Tooltip title="Add Payment">
          <GuardedButton type="text" size="small" icon={<PlusOutlined />} className="fee-action-btn fee-action-btn--add"
            onClick={(e) => { e.stopPropagation(); handleAddFee(record); setIsStudent(true); }} />
        </Tooltip>
      ),
    }] : []),
  ];

  const feeEntryColumns = [
    {
      title: '#',
      key: 'index',
      width: 40,
      render: (_: any, __: any, index: number) => (
        <span className="fee-entry-idx">{index + 1}</span>
      ),
    },
    {
      title: 'Paid Amount',
      dataIndex: 'paidAmount',
      key: 'paidAmount',
      render: (amount: number) => (
        <span className="fee-amount fee-amount--green fee-amount--sm">
          ₹{amount?.toLocaleString('en-IN') || '0'}
        </span>
      ),
    },
    {
      title: 'Balance After',
      dataIndex: 'pendingAmount',
      key: 'pendingAmount',
      render: (amount: number) => (
        <span className={`fee-amount fee-amount--sm ${amount > 0 ? 'fee-amount--red' : 'fee-amount--green'}`}>
          ₹{amount?.toLocaleString('en-IN') || '0'}
          {amount === 0 && <Tag color="success" style={{ marginLeft: 6, fontSize: 10, padding: '0 4px' }}>CLEARED</Tag>}
        </span>
      ),
    },
    {
      title: 'Payment Date',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      render: (date: string) => (
        <span className="fee-date-cell fee-date-cell--sm">
          <CalendarOutlined />
          {date ? dayjs(date).format('DD MMM YYYY') : '—'}
        </span>
      ),
    },
    {
      title: 'Mode',
      dataIndex: 'paymentMode',
      key: 'paymentMode',
      render: (mode: string) => {
        const map: Record<string, string> = {
          CASH: 'green', UPI: 'blue', CARD: 'purple',
          NET_BANKING: 'cyan', CHEQUE: 'orange', BANK_TRANSFER: 'geekblue',
        };
        return mode ? (
          <Tag color={map[mode] || 'default'} style={{ fontSize: 11, fontWeight: 600, border: '1px solid' }}>
            <CreditCardOutlined style={{ marginRight: 3 }} />
            {mode.replace(/_/g, ' ')}
          </Tag>
        ) : <span className="fee-empty-cell">—</span>;
      },
    },
    {
      title: 'Receipt No.',
      dataIndex: 'receiptNumber',
      key: 'receiptNumber',
      render: (text: string) => (
        text
          ? <Typography.Text code style={{ fontSize: 11 }}>{text}</Typography.Text>
          : <span className="fee-empty-cell">—</span>
      ),
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      ellipsis: true,
      render: (text: string) => (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {text || <span className="fee-empty-cell">—</span>}
        </Typography.Text>
      ),
    },
    {
      title: 'Recorded On',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {date
            ? new Date(date).toLocaleString('en-GB', {
              day: '2-digit',
              month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true
            })
            : '—'}
        </Typography.Text>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_: any, record: any) => (
        <Space size={4}>

          <Tooltip title="Delete payment">
            <Button type="text" size="small" danger
              icon={<DeleteOutlined />}
              onClick={(e) => { e.stopPropagation(); setFeeRecordToDelete(record.key); }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const canEditExpense = (role === 'ADMIN' || role === 'SUPER_ADMIN') && pageAccess.isEdit;
  const canDeleteExpense = (role === 'ADMIN' || role === 'SUPER_ADMIN');

  // Full detail from the view endpoint, matched to the currently-expanded row.
  const expenseDetailData = (selectedExpense && String(selectedExpense._id) === String(expandedExpenseKey))
    ? selectedExpense
    : null;
  const expenseDetailLoading = Boolean(expenseView?.isLoading) && !expenseDetailData;

  const renderExpandedExpense = (record: any) => {
    const detail = expenseDetailData ?? {
      _id: record.key,
      expenseName: record.expenseName,
      amount: record.amount,
      categoryName: record.categoryName,
      serviceName: record.serviceName,
      studentName: record.studentName,
      expenseDate: record.expenseDate,
      description: record.description,
      createdAt: record.createdAt,
      updatedAt: record.updatedAt,
      createdBy: record.createdBy,
    };
    return (
      <ExpenseDetailPanel
        detail={detail}
        loading={expenseDetailLoading}
        isEditing={expenseEditMode}
        canEdit={canEditExpense}
        canDelete={canDeleteExpense}
        saving={Boolean(expenseData?.isLoading)}
        form={expenseEditForm}
        categoryOptions={categoryOptions}
        serviceOptions={serviceOptions}
        studentOptions={studentOptions}
        onEdit={() => handleExpensePanelEdit(detail)}
        onCancel={handleExpensePanelCancel}
        onSave={() => handleExpensePanelSave(detail)}
        onDelete={() => setRecordToDelete(detail?._id ?? record.key)}
      />
    );
  };

  const tabItems = [
    {
      key: 'expense',
      label: (
        <span>
          <WalletOutlined style={{ marginRight: 8 }} />
          Expense
        </span>
      ),
      children: (
        <>
          <div className='expense-wrapper'>
            <div className="expense-toolbar">
              <div className="expense-search-section">
                <Input placeholder="Search expenses..." prefix={<SearchOutlined />} value={searchText}
                  onChange={(e) => setSearchText(e.target.value)} className="expense-search-input" allowClear />
                <FilterPopover fields={filterFields} onApply={handleApplyFilters} columns={2} appliedCount={Object.keys(appliedFilters).length}
                  placement='bottomLeft' onClear={handleClearFilters} initialValues={appliedFilters} />
              </div>
            </div>
            {pageAccess.isAdd &&
              <div className="expense-add-button-wrapper">
                <GuardedButton type="primary" icon={<PlusOutlined />} onClick={handleAdd} style={{ width: 'auto' }}>Add Expense</GuardedButton>
              </div>}
          </div>

          <div className="expense-table-wrapper">
            {mutableExpenses.length === 0 ? (
              <Empty
                description="No expenses found"
                className="expense-empty"
              />
            ) : (
              < div className="driveMynt-card">
                <TableData className='responsive-table expense-table expense-table--expandable' columns={columns}
                  rowKey={(record: any) => String(record.key)} dataSource={mutableExpenses}
                  pagination={false} expandable={{
                    expandedRowKeys: expandedExpenseKey ? [expandedExpenseKey] : [],
                    onExpand: handleExpenseExpand,
                    expandedRowRender: renderExpandedExpense,
                    expandRowByClick: pageAccess.isView,
                    rowExpandable: () => pageAccess.isView,
                    showExpandColumn: pageAccess.isView,
                    columnWidth: 48,
                    expandIcon: ({ expanded, onExpand, record }: any) => (
                      <span
                        className={`cat-expand-ic${expanded ? ' cat-expand-ic--open' : ''}`}
                        onClick={(e) => { e.stopPropagation(); onExpand(record, e); }}
                      >
                        <RightOutlined />
                      </span>
                    ),
                  }}
                  onRow={(record: any) => ({
                    className: String(record.key) === String(expandedExpenseKey) ? 'cat-row-active' : '',
                    style: { cursor: pageAccess.isView ? 'pointer' : 'default' },
                  })}
                />
                <div className="expense-pagination-wrapper">
                  <Paginations current={currentPage} pageSize={pageSize} total={totalRecords} callback={handlePageChange} />
                </div>
              </div>
            )}
          </div>
        </>
      ),
    },
    {
      key: 'fees',
      label: (
        <span>
          <DollarOutlined style={{ marginRight: 8 }} />
          Fees
        </span>
      ),
      children: (
        <>
          <div className='expense-wrapper'>
            <div className="expense-toolbar">
              <div className="expense-search-section">
                <Input placeholder="Search fees..." prefix={<SearchOutlined />} value={feeSearchText}
                  onChange={(e) => setFeeSearchText(e.target.value)} className="expense-search-input" allowClear />
                <FilterPopover fields={feeFilterFields} onApply={handleApplyFeeFilters} columns={2} appliedCount={Object.keys(appliedFeeFilters).length}
                  placement='bottomLeft' onClear={handleClearFeeFilters} initialValues={appliedFeeFilters} />
              </div>
            </div>
            <div className="expense-add-button-wrapper">
              <Dropdown menu={{ items: feeExportMenuItems }} placement="bottomRight">
                <Button icon={<DownloadOutlined />} loading={exportFeeState?.isLoading} style={{ marginRight: 18 }}>
                  Export
                </Button>
              </Dropdown>
              {pageAccess.isAdd &&
                <GuardedButton type="primary" icon={<PlusOutlined />} onClick={() => { handleAddFee(); setIsStudent(false); }} style={{ width: 'auto' }}>Record Fee Payment</GuardedButton>}
            </div>
          </div>

          <div className="expense-table-wrapper">
            {mutableFees.length === 0 ? (
              <Empty
                description="No fee records found"
                className="expense-empty"
              />
            ) : (
              <div className="driveMynt-card">
                <TableData
                  columns={feeColumns}
                  dataSource={mutableFees}
                  pagination={false}
                  className="responsive-table expense-table expense-table--expandable"
                  expandable={{
                    expandedRowRender: (record: QueryFee) => (
                      <FeeDetailPanel
                        record={record}
                        role={role as string}
                        entryColumns={feeEntryColumns}
                        onDeleteEntry={(key: string) => setFeeRecordToDelete(key)}
                      />
                    ),
                    rowExpandable: () => true,
                    expandRowByClick: false,
                    columnWidth: 48,
                    expandIcon: ({ expanded, onExpand, record }: any) => (
                      <span className={`cat-expand-ic${expanded ? ' cat-expand-ic--open' : ''}`} onClick={(e) => { e.stopPropagation(); onExpand(record, e); }} >
                        <RightOutlined />
                      </span>
                    ),
                  }}
                />
                <div className="expense-pagination-wrapper">
                  <Paginations current={feeCurrentPage} pageSize={feePageSize} total={totalFeeRecords} callback={handleFeePageChange} />
                </div>
              </div>
            )}
          </div>
        </>
      ),
    },
  ];




  useEffect(() => {
    if (getExpense?.isSuccess || getExpense?.isError) {
      setInitialLoaded(true);
    }
  }, [getExpense?.isSuccess, getExpense?.isError]);

  useEffect(() => {
    if (getFee?.isSuccess || getFee?.isError) {
      setInitialFeeLoaded(true);
    }
  }, [getFee?.isSuccess, getFee?.isError]);

  const isCurrentTabLoading =
    (activeTab === 'expense' && getExpense?.isLoading) ||
    (activeTab === 'fees' && getFee?.isLoading);

  if (isCurrentTabLoading) {
    return <SpinnerLoader />;
  }

  return (
    <div className="expense-page-wrapper">
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} className="expense-tabs" />
      <ExpenseFormModal open={isModalOpen} isLoading={Boolean(expenseData?.isLoading)} title={isEditMode ? 'Edit Expense' : 'Add Expense'} onCancel={handleModalCancel}
        onFinish={handleExpenseFormFinish} form={form} />
      <FeeFormModal open={isFeeModalOpen} title="Record Fee Payment" onCancel={handleFeeModalCancel}
        onFinish={handleFeeFormFinish} isLoading={Boolean(feeData?.isLoading)} form={feeForm} preSelectedData={selectedFeeRecord} 
        isSingleStudent={isStudent}/>
      <ConfirmModal visible={!!recordToDelete} onOk={handleConfirmDelete} onCancel={handleCancelDelete} title="Delete Expense"
        content="Are you sure you want to delete this expense? This action cannot be undone." />
      <ConfirmModal visible={!!feeRecordToDelete} onOk={handleConfirmFeeDelete} onCancel={handleCancelFeeDelete} title="Delete Fee Record"
        content="Are you sure you want to delete this fee record? This action cannot be undone." />
    </div>
  );
};

export default Expenses;
